'use client';
import Image from 'next/image';
import { ReactNode, useState } from 'react';
import React from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';
import LocalizedLink from '../LocalizedLink';

export interface AdvantagesCustomV3Props {
  id?: string;
  color?: 'violet' | 'green' | 'blue';
  topSubtitle?: ReactNode | SerializedEditorState;
  topSubtitle_2?: ReactNode | SerializedEditorState;
  logo?: string | { url: string; alt: string };
  title: ReactNode | SerializedEditorState;
  title_2?: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  videoLink?: string | { url: string };
  youtubeLink?: string;
  sideText: ReactNode | SerializedEditorState;
  buttonText?: string;
  buttonLink?: string;
}

export default function AdvantagesCustomV3({
  id,
  color = 'green',
  topSubtitle,
  topSubtitle_2,
  logo,
  title,
  title_2,
  subtitle,
  videoLink = { url: 'https://academy.ap.education/assets/video/promoEng.mp4' },
  sideText,
  buttonText,
  buttonLink,
}: AdvantagesCustomV3Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';

  return (
    <div id={id} data-meta="AdvantagesCustomV3">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 my-14">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col-reverse md:flex-row md:justify-between gap-12">
            <div className="flex flex-col flex-grow gap-4">
              {(topSubtitle || topSubtitle_2) && (
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  {topSubtitle && (
                    <CustomRichText data={topSubtitle} tag="span" className="text-white text-xl" />
                  )}
                  {topSubtitle_2 && (
                    <CustomRichText
                      data={topSubtitle_2}
                      tag="span"
                      className="text-white text-xl uppercase font-bold lg:transform lg:translate-y-[30px]"
                    />
                  )}
                </div>
              )}
              <div className="flex justify-between items-center">
                <h2 className="flex gap-2 font-bold">
                  <CustomRichText data={title} tag="div" className="text-white text-4xl" />

                  {title_2 && (
                    <CustomRichText
                      data={title_2}
                      tag="div"
                      className={`custom-gradient-${color}-text text-4xl`}
                    />
                  )}
                </h2>
                {logo && (
                  <Image
                    src={typeof logo === 'string' ? logo : logo.url}
                    alt={typeof logo === 'string' ? '' : logo.alt}
                    width={150}
                    height={80}
                  />
                )}
              </div>
              {subtitle && (
                <CustomRichText
                  data={subtitle}
                  tag="div"
                  className={`text-white ${
                    id === 'conf-university'
                      ? 'self-end text-right italic whitespace-pre-line w-full sm:w-2/3 '
                      : 'self-center text-center w-full lg:w-2/3'
                  }`}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
            <div className="lg:w-1/2 mask-dir large">
              <video
                className="w-full h-full object-cover max-lg:rounded-3xl"
                src={typeof videoLink === 'string' ? videoLink : videoLink.url}
                loop
                playsInline
                autoPlay
                controls={false}
                muted
              ></video>
            </div>
            <div className="lg:w-1/2">
              <div className="text-white">
                <CustomRichText data={sideText} tag="div" />
              </div>
            </div>
          </div>

          {buttonLink && buttonText && (
            <LocalizedLink
              href={buttonLink}
              className={`${backgroundColorClass} uppercase text-white rounded-full py-4 px-6 sm:px-14 w-fit font-semibold m-auto`}
            >
              {buttonText}
            </LocalizedLink>
          )}
        </div>
      </div>
    </div>
  );
}
